import {
  type MutationOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { App } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useShallow } from 'zustand/shallow';

import { api } from '@/api';
import { END_POINT } from '@/constants/end-point.constants';
import { ORGANIZATION_QUERY_KEYS } from '@/constants/query-keys.constants';
import { ROUTES_PATH } from '@/constants/routes.constants';
import { useUserInformation } from '@/hooks/use-user-information';
import { useCommonStore } from '@/store/common.store';
import { UserRoleEnum } from '@/types/auth.types';

import type { OrganizationRequest } from '../types';

function parsedRequestBody(requestBody: OrganizationRequest) {
  const {
    operation_end_date: operationEndDate,
    operation_start_date: operationStartDate,
    ...rest
  } = requestBody;
  return {
    ...rest,
    operation_start_date: operationStartDate?.startOf('day').toISOString(),
    operation_end_date: operationEndDate?.endOf('day').toISOString(),
  };
}

const registerOrganization = (requestBody: OrganizationRequest) =>
  api.post(END_POINT.organizations, parsedRequestBody(requestBody));

export const useRegisterOrganization = () => {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const { setIsFormChanged, setIsProcessing } = useCommonStore(
    useShallow(({ setIsFormChanged, setIsProcessing }) => ({
      setIsFormChanged,
      setIsProcessing,
    })),
  );

  return useMutation({
    mutationFn: (requestBody: OrganizationRequest) =>
      registerOrganization(requestBody),
    onMutate: () => setIsProcessing(true),
    onSettled: () => setIsProcessing(false),
    onSuccess() {
      setIsFormChanged(false);
      Promise.resolve().then(() => navigate(ROUTES_PATH.organizationList));
      message.success('チェーンの担当者に招待メールを送信しました');
    },
  });
};

const updateOrganization = (id: string, requestBody: OrganizationRequest) =>
  api.put(END_POINT.organizationDetail(id), parsedRequestBody(requestBody));

export const useUpdateOrganization = () => {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const { saleAreaId, role } = useUserInformation();
  const { setIsFormChanged, setIsProcessing, setIsFormUpdated } =
    useCommonStore(
      useShallow(({ setIsFormChanged, setIsProcessing, setIsFormUpdated }) => ({
        setIsFormChanged,
        setIsProcessing,
        setIsFormUpdated,
      })),
    );

  return useMutation({
    mutationFn: ({
      id,
      requestBody,
    }: {
      id: string;
      requestBody: OrganizationRequest;
    }) => updateOrganization(id, requestBody),
    onMutate: () => setIsProcessing(true),
    onSettled: () => setIsProcessing(false),
    onSuccess: async (
      _,
      { id, requestBody: { sale_area_ids: saleAreaIds } },
    ) => {
      setIsFormChanged(false);
      setIsFormUpdated(true);

      const shouldRedirect =
        role === UserRoleEnum.sbs_sales &&
        !saleAreaIds.includes(Number(saleAreaId));

      if (shouldRedirect) {
        Promise.resolve().then(() => navigate(ROUTES_PATH.organizationList));
      } else {
        await queryClient.invalidateQueries({
          queryKey: ORGANIZATION_QUERY_KEYS.detail(id),
        });
      }
      message.success('更新しました');
    },
  });
};

const deleteOrganization = (id: number) =>
  api.delete(END_POINT.organizations, { data: { ids: [id] } });

export const useDeleteOrganization = ({
  onSuccess,
}: MutationOptions<unknown, unknown, unknown>) => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const setIsProcessing = useCommonStore(state => state.setIsProcessing);

  return useMutation({
    mutationFn: (id: number) => deleteOrganization(id),
    onMutate: () => setIsProcessing(true),
    onSettled: () => setIsProcessing(false),
    onSuccess: async (...arguments_) => {
      await queryClient.invalidateQueries({
        queryKey: ORGANIZATION_QUERY_KEYS.organizationsOfMe(),
      });
      onSuccess?.(...arguments_);
      message.success('削除しました');
    },
  });
};
